import { useUiStore } from '@/stores/ui.store';
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { useStorage } from '@vueuse/core';
import sharedRoutes from '@/router/shared.routes';
import { useOfferContext } from '@/composables/useOfferContext';
import {
  BUSINESS_UNITS,
  getBusinessUnitConfig,
  getBusinessUnitLegacyRoutes,
  getBusinessUnitRoutes,
} from '@/modules/bu/registry';
import { getBuFromDomain, getStoredBu, normalizeBusinessUnit } from '@/utils/bu.utils';
import { debugMiddlewares } from '@/middlewares/debug.middlewares';

const buRoutes = BUSINESS_UNITS.map((bu) => ({
  path: bu.basePath,
  children: getBusinessUnitRoutes(bu.key),
}));
const legacyRoutes = BUSINESS_UNITS.flatMap((bu) => getBusinessUnitLegacyRoutes(bu.key));

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'ChooseBusinessUnitView',
      component: () => import('@/views/common/ChooseBusinessUnitView.vue'),
      beforeEnter: (to) => {
        const offer = typeof to.query.offer === 'string' ? to.query.offer : undefined;
        const domainBu = getBuFromDomain(window.location.origin);
        const domainConfig = getBusinessUnitConfig(domainBu);
        if (domainConfig) {
          if (offer) return { name: domainConfig.loginRouteName, query: { offer } };
          return { path: domainConfig.basePath, query: to.query };
        }

        const authStore = useAuthStore();
        if (authStore.access_token) {
          const storedBu = getStoredBu();
          const storedConfig = getBusinessUnitConfig(storedBu);
          if (storedConfig) {
            if (offer) return { name: storedConfig.loginRouteName, query: { offer } };
            return { path: storedConfig.basePath, query: to.query };
          }

          // Fallback: derive BU from the order summary if available.
          const orderSummaryStore = useOrderSummaryStore();
          const order_bu = normalizeBusinessUnit(orderSummaryStore.orderSummary?.business_unit);
          if (order_bu) {
            const config = getBusinessUnitConfig(order_bu);
            if (config) {
              if (offer) return { name: config.loginRouteName, query: { offer } };
              return { path: config.basePath, query: to.query };
            }
          }
        }
      },
    },
    ...buRoutes,
    ...legacyRoutes,
    ...sharedRoutes,
  ],
});

router.afterEach(() => {
  const uiStore = useUiStore();
  uiStore.setLoading(false);
});

// Keep offer query in sync with app-level context across all navigations.
router.beforeEach((to) => {
  const offerContext = useOfferContext();
  offerContext.syncFromRoute(to);

  return offerContext.ensureOfferInQuery(to);
});

router.beforeEach((to, from) => {
  const lastPath = useStorage('app_last_path', '', sessionStorage);
  if (to.path === from.path) {
    return;
  }

  lastPath.value = from.path;
  return;
});

router.beforeEach((to, from, next) => {
  debugMiddlewares.syncDebugStatus(to, from, next);
});

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location.href = to.fullPath;
    }
  }
});

export default router;
